import React, { useState } from 'react';
import { Container, Form, Button, Card, Alert } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { createUserWithEmailAndPassword, updateProfile, signInWithPopup } from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db, googleProvider } from '../firebase.ts';
import sprechheldenHeld from '../assets/sprechheldenHeld.png';

function RegistrationPage({ setIsAuthenticated, setHasSubscription }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newsletter, setNewsletter] = useState(false);
  const [acceptedAGB, setAcceptedAGB] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate name is provided
    if (!name.trim()) {
      setError('Bitte gib deinen Namen ein');
      setLoading(false);
      return;
    }

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      setLoading(false);
      return;
    }

    // Validate password length
    if (password.length < 6) {
      setError('Passwort muss mindestens 6 Zeichen lang sein');
      setLoading(false);
      return;
    }

    // Validate AGB acceptance
    if (!acceptedAGB) {
      setError('Du musst die AGB akzeptieren, um dich zu registrieren');
      setLoading(false);
      return;
    }

    try {
      // Create user with Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Create user document in Firestore
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        id: user.uid,
        email: user.email,
        displayName: name.trim(),
        subscription: {
          type: 'none',
          name: 'Kein Abonnement',
          pricePerMonth: 0,
          features: [],
          billingPeriod: 'monthly',
          isActive: false
        },
        role: 'family', // Default to family role
        createdAt: new Date(),
        settings: {
          language: 'de',
          notifications: true,
          newsletter: newsletter
        },
        familyMembers: [],
        hasSeenWelcomeModal: false,
        hasSeenModuleIntroModal: false
      });
      
      // Update auth display name
      await updateProfile(user, {
        displayName: name.trim()
      });

      
      // Create Stripe customer
      try {
        const idToken = await user.getIdToken();
        const functionRegion = process.env.REACT_APP_FUNCTIONS_REGION || 'europe-west3';
        const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID;
        const response = await fetch(
          `https://${functionRegion}-${projectId}.cloudfunctions.net/createStripeCustomer`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`
            },
            body: JSON.stringify({
              displayName: name.trim(),
              email: user.email
            })
          }
        );

        if (response.ok) {
          const result = await response.json();
          
          // Update user document with Stripe customer ID
          await updateDoc(doc(db, 'users', user.uid), {
            stripeCustomerId: result.customerId
          });
        } else {
          console.warn('Failed to create Stripe customer, but user registration succeeded');
        }
      } catch (stripeError) {
        console.warn('Error creating Stripe customer:', stripeError);
        // Don't fail registration if Stripe customer creation fails
      }
      
      // Registration successful
      setIsAuthenticated(true);
      setHasSubscription(false); // New users need to subscribe
      navigate('/subscription');
    } catch (error) {
      console.error('Registration error:', error);
      
      // Handle specific Firebase errors
      switch (error.code) {
        case 'auth/email-already-in-use':
          setError('Diese E-Mail-Adresse wird bereits verwendet');
          break;
        case 'auth/invalid-email':
          setError('Ungültige E-Mail-Adresse');
          break;
        case 'auth/weak-password':
          setError('Passwort ist zu schwach');
          break;
        default:
          setError('Registrierung fehlgeschlagen. Bitte versuche es erneut.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      // Sign in with Google
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      // Check if user document exists
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
              // If user document doesn't exist, create it (new registration)
        if (!userDoc.exists()) {
          await setDoc(doc(db, 'users', user.uid), {
            uid: user.uid,
            id: user.uid,
            email: user.email,
            displayName: user.displayName || 'Google User',
            subscription: {
              type: 'none',
              name: 'Kein Abonnement',
              pricePerMonth: 0,
              features: [],
              billingPeriod: 'monthly',
              isActive: false
            },
            role: 'family',
            createdAt: new Date(),
            settings: {
              language: 'de',
              notifications: true,
              newsletter: false
            },
            familyMembers: [],
            hasSeenWelcomeModal: false,
            hasSeenModuleIntroModal: false
          });

          // Create Stripe customer for Google user
          try {
            const idToken = await user.getIdToken();
            const functionRegion = process.env.REACT_APP_FUNCTIONS_REGION || 'europe-west3';
            const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID;
            const response = await fetch(
              `https://${functionRegion}-${projectId}.cloudfunctions.net/createStripeCustomer`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${idToken}`
                },
                body: JSON.stringify({
                  displayName: user.displayName || 'Google User',
                  email: user.email
                })
              }
            );

            if (response.ok) {
              const result = await response.json();
              
              // Update user document with Stripe customer ID
              await updateDoc(doc(db, 'users', user.uid), {
                stripeCustomerId: result.customerId
              });
            } else {
              console.warn('Failed to create Stripe customer for Google user, but registration succeeded');
            }
          } catch (stripeError) {
            console.warn('Error creating Stripe customer for Google user:', stripeError);
            // Don't fail registration if Stripe customer creation fails
          }
          
          // New Google user needs subscription
          setIsAuthenticated(true);
          setHasSubscription(false);
          navigate('/subscription');
        } else {
          // Existing user trying to register - redirect to login
          setError('Du hast bereits ein Konto. Bitte melde dich an.');
        }
    } catch (error) {
      console.error('Google sign-in error:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);

      switch (error.code) {
        case 'auth/popup-closed-by-user':
          setError('Registrierung abgebrochen');
          break;
        case 'auth/cancelled-popup-request':
          setError('Registrierung abgebrochen');
          break;
        case 'auth/unauthorized-domain':
          setError('Diese Domain ist nicht für Google-Registrierung autorisiert. Bitte kontaktiere den Administrator.');
          break;
        default:
          setError(`Google-Registrierung fehlgeschlagen. Bitte versuche es erneut. (Fehlercode: ${error.code || 'unbekannt'})`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="min-vh-100 d-flex align-items-center justify-content-center">
      <div className="registration-container">
        <Card className="shadow-sm">
          <Card.Body className="p-5" style={{ borderRadius: 'var(--radius-md)' }}>
            <div className="d-flex align-items-center justify-content-center mb-4">
              <img 
                src={sprechheldenHeld} 
                alt="Sprechhelden Hero" 
                style={{ width: '60px', height: 'auto' }}
                className="login-hero-image me-3"
              />
              <h2 className="mb-0">Sprechhelden</h2>
            </div>
            <p className="text-center text-muted mb-4">
              Erstelle dein Konto und starte deine Reise
            </p>
            
            {error && (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            )}

            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Dein Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  disabled={loading}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>E-Mail Adresse</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="name@beispiel.de"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Passwort</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Mindestens 6 Zeichen"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                />
                <Form.Text className="text-muted">
                  Das Passwort muss mindestens 6 Zeichen lang sein
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>Passwort bestätigen</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Passwort wiederholen"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={loading}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="newsletter-checkbox"
                  label="Ich möchte den Newsletter erhalten (optional)"
                  checked={newsletter}
                  onChange={(e) => setNewsletter(e.target.checked)}
                  disabled={loading}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Check
                  type="checkbox"
                  id="agb-checkbox"
                  label={<span>Ich akzeptiere die <a href="/agb" target="_blank" rel="noopener noreferrer">AGB</a> *</span>}
                  checked={acceptedAGB}
                  onChange={(e) => setAcceptedAGB(e.target.checked)}
                  disabled={loading}
                  required
                />
              </Form.Group>

              <Button 
                variant="primary" 
                type="submit" 
                className="w-100 registration-cta-button"
                style={{ 
                  backgroundColor: 'var(--clr-green-500)', 
                  border: '2px solid var(--clr-green-600)',
                  boxShadow: '0 4px 12px rgba(136, 216, 176, 0.3)',
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  padding: '14px 24px',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                disabled={loading}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.target.style.backgroundColor = 'var(--clr-green-400)';
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 16px rgba(136, 216, 176, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.target.style.backgroundColor = 'var(--clr-green-500)';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(136, 216, 176, 0.3)';
                  }
                }}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Registriere...
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="me-2">
                      <path d="M9 12l2 2 4-4"></path>
                      <path d="M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.745 3.745 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.745 3.745 0 013.296-1.043A3.745 3.745 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.745 3.745 0 013.296 1.043 3.745 3.745 0 011.043 3.296A3.745 3.745 0 0121 12z"></path>
                    </svg>
                    Konto erstellen
                  </>
                )}
              </Button>
            </Form>

            <div className="d-flex align-items-center my-4">
              <hr className="flex-grow-1" />
              <span className="px-3 text-muted">oder</span>
              <hr className="flex-grow-1" />
            </div>

            <Button 
              variant="outline-dark" 
              className="w-100 d-flex align-items-center justify-content-center"
              onClick={handleGoogleSignIn}
              disabled={loading}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" className="me-2">
                <g fill="none" fillRule="evenodd">
                  <path d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4" />
                  <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853" />
                  <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
                  <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335" />
                </g>
              </svg>
              Mit Google registrieren
            </Button>

            <p className="text-center text-muted mt-4 mb-0">
              Bereits ein Konto? <Link to="/login" className="btn btn-link p-0 text-decoration-none">Anmelden</Link>
            </p>
          </Card.Body>
        </Card>
      </div>
    </Container>
  );
}

export default RegistrationPage; 